import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/contexts/AuthContext";

const STATUS_LABELS = {
  pending: "Scheduled", publishing: "Processing — check before resubmitting",
  published: "Published", failed: "Needs review",
  unknown: "Publication outcome uncertain", cancelled: "Cancelled",
};

export default function SocialScheduleList() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState(false);
  const [truncated, setTruncated] = useState(false);
  const pending = useRef(false);
  const epoch = useRef(0);
  const load = async () => {
    const current = ++epoch.current;
    setLoading(true);
    setError(false);
    try {
      const { data } = await axios.get(`${API}/oauth/meta/scheduled`, { params: { status: filter } });
      if (current !== epoch.current) return;
      setRows(Array.isArray(data?.posts) ? data.posts : []);
      setTruncated(!!data?.has_more);
    } catch {
      if (current === epoch.current) setError(true);
    } finally {
      if (current === epoch.current) setLoading(false);
    }
  };
  useEffect(() => {
    load();
    return () => { epoch.current += 1; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);
  const cancel = async (post) => {
    if (pending.current || !window.confirm("Cancel this scheduled post? This does not remove a post already published.")) return;
    pending.current = true;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/oauth/meta/scheduled/${encodeURIComponent(post.id)}/cancel`);
      if (!data?.ok) throw new Error();
      toast.success("Scheduled post cancelled.");
    } catch {
      toast.error("Cancellation could not be confirmed. The post may already be processing. Refresh and check its status.");
    } finally {
      await load();
      pending.current = false;
      setBusy(false);
    }
  };
  return (
    <section className="bg-white border border-[#eee] rounded-2xl p-5" data-testid="mc-scheduled-posts">
      <div className="flex justify-between gap-3 items-center">
        <h2 className="font-semibold">Scheduled Facebook &amp; Instagram posts</h2>
        <button className="zy-btn-outline" onClick={load} disabled={loading || busy}>Refresh</button>
      </div>
      <p className="text-sm text-[#666] mt-2">Scheduled posts and their recorded outcomes for this workspace. Dates and times are shown in your local time zone.</p>
      <label className="block mt-4 text-sm">
        Show status
        <select value={filter} disabled={busy}
          onChange={(event) => setFilter(event.target.value)}
          className="ml-2 border rounded-md p-2" data-testid="mc-schedule-status">
          {Object.entries(STATUS_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select>
      </label>
      {loading ? <p role="status" className="mt-4 text-sm">Loading scheduled posts…</p>
        : error ? <p role="alert" className="mt-4 text-sm">Scheduled posts could not be loaded. Refresh to try again.</p>
        : rows.length === 0 ? <p className="mt-4 text-sm">No posts with this status.</p>
        : <ul className="space-y-3 mt-4">{rows.map((post) => {
          const when = new Date(post.scheduled_at);
          const validDate = Number.isFinite(when.getTime());
          return (
            <li key={post.id} className="border rounded-lg p-4">
              <p className="text-sm font-semibold mb-2">{STATUS_LABELS[post.status] || "Status requires review"}</p>
              <p className="font-medium text-sm">{validDate ? when.toLocaleString(undefined, { dateStyle: "full", timeStyle: "long" }) : "Time requires review"}</p>
              <p className="text-xs text-[#666] mt-1">
                {[post.target_fb && "Facebook", post.target_ig && "Instagram"].filter(Boolean).join(" and ")}
                {" · Page "}{post.page_id}
              </p>
              <p className="whitespace-pre-wrap break-words mt-3 text-sm">{post.message}</p>
              {post.image_url && <p className="text-xs text-[#666] mt-2">Image attached</p>}
              {post.status === "pending" && validDate && when.getTime() <= Date.now() && <p className="text-xs text-amber-800 mt-2">The scheduled time has passed. Refresh and check the publication status.</p>}
              {["unknown", "publishing"].includes(post.status) && <p className="text-sm text-amber-800 mt-2">
                Check the social account before creating another post. This post may already be published.
              </p>}
              {post.status === "failed" && <p className="text-sm mt-2">
                Review the account connection, approval and photo before scheduling again.
              </p>}
              {post.status === "pending" && <button className="zy-btn-outline mt-3" disabled={busy || loading} onClick={() => cancel(post)}>Cancel scheduled post</button>}
            </li>
          );
        })}</ul>}
      {!loading && truncated && <p role="status" className="text-sm mt-4">Showing the first 200 posts with this status. Additional posts are not displayed.</p>}
    </section>
  );
}
